import React, { useEffect } from 'react';
import Input from '~/components/Inputs/Input';
import InputMask from '~/components/Inputs/InputMask';
import InputSelect from '~/components/Inputs/InputSelect';
import TextAreaWithCounter from '~/components/Inputs/TextAreaWithCounter';
import { Row, Col, FormGroup, Label, CustomInput } from 'reactstrap';
import { Scope } from '@unform/core';
import { useAddressForm } from '~/hooks/useAddressForm';

export default function AddressFields({
  formRef,
  initialAddress,
  disabled = false,
  disabledFields = {},
}) {
  const {
    cityList,
    selectedState,
    selectedCity,
    noNumber,
    addressNumber,
    searchCEP,
    setSelectedState,
    setSelectedCity,
    setNoNumber,
    setAddressNumber,
    initializeAddress,
  } = useAddressForm(formRef, initialAddress);

  const isDisabled = fieldName =>
    Boolean(disabledFields?.[fieldName] ?? disabled);

  useEffect(() => {
    initializeAddress();
  }, [initializeAddress]);

  const clearError = field => {
    formRef?.current?.setFieldError?.(field, null);
  };

  // ✅ quando o CEP autopreenche, o onChange do select não dispara.
  // então limpamos o erro assim que o selectedState/selectedCity mudar.
  useEffect(() => {
    if (selectedState?.value || selectedState?.label) {
      formRef?.current?.setFieldError?.('address.state', null);
    }
  }, [selectedState, formRef]);

  useEffect(() => {
    if (selectedCity?.value || selectedCity?.label) {
      formRef?.current?.setFieldError?.('address.city', null);
    }
  }, [selectedCity, formRef]);

  return (
    <Scope path="address">
      <Row className="w-100 mx-0 font-size-20 font-weight-700 text-gray-700 my-4">
        Endereço
      </Row>

      <Row className="w-100 mx-0">
        <Col className="px-0">
          <FormGroup>
            <Label>CEP</Label>
            <InputMask
              disabled={isDisabled('cep')}
              name="cep"
              placeholder="CEP"
              mask="99999-999"
              maskChar={null}
              onChange={e => {
                const cepValue = e.target.value;

                clearError('address.cep');

                if (cepValue.length === 9) searchCEP(cepValue);
              }}
            />
          </FormGroup>
        </Col>
      </Row>

      <Row className="w-100 mx-0">
        <Col lg={6} className="px-0 pr-lg-2">
          <FormGroup>
            <Label>Logradouro</Label>
            <Input
              disabled={isDisabled('street_name')}
              name="street_name"
              placeholder="Logradouro"
              type="text"
              onChange={() => clearError('address.street_name')}
            />
          </FormGroup>
        </Col>

        <Col lg={6} className="px-0 pl-lg-2">
          <Label>Número</Label>
          <CustomInput
            type="switch"
            id="noNumberSwitch"
            name="noNumber"
            label="Sem Número"
            checked={noNumber}
            className="text-primary float-right"
            onChange={() => {
              clearError('address.number');

              if (noNumber) {
                setAddressNumber('');
              } else {
                setAddressNumber('sn');
              }
              setNoNumber(!noNumber);
            }}
          />

          <FormGroup>
            <Input
              disabled={isDisabled('number')}
              name="number"
              placeholder="Número"
              type="text"
              onChange={({ target: { value } }) => {
                clearError('address.number');
                setAddressNumber(value);
              }}
              value={noNumber ? 'sn' : addressNumber}
            />
          </FormGroup>
        </Col>
      </Row>

      <Row className="w-100 mx-0">
        <Col lg={6} className="px-0 pr-lg-2">
          <FormGroup>
            <Label>Bairro</Label>
            <Input
              disabled={isDisabled('neighborhood')}
              name="neighborhood"
              placeholder="Bairro"
              type="text"
              onChange={() => clearError('address.neighborhood')}
            />
          </FormGroup>
        </Col>

        <Col lg={6} className="px-0 pl-lg-2">
          <TextAreaWithCounter
            disabled={isDisabled('complement')}
            name="complement"
            placeholder="Complemento"
            title="Complemento"
            maxSize={50}
            onChange={() => clearError('address.complement')}
          />
        </Col>
      </Row>

      <Row className="w-100 mx-0">
        <Col lg={6} className="px-0 pr-lg-2">
          <FormGroup>
            <Label>Estado</Label>
            <InputSelect
              isDisabled={disabled}
              name="state"
              options={cityList.stateList}
              onChange={selectedOption => {
                clearError('address.state');
                setSelectedState(selectedOption);
              }}
              value={selectedState}
              placeholder="Estado"
              styles={{
                control: (base, state) => ({
                  ...base,
                  backgroundColor: state.isDisabled
                    ? '#ced4da9f !important'
                    : '#fff',
                  cursor: state.isDisabled ? 'not-allowed' : base.cursor,
                  opacity: state.isDisabled ? 0.65 : 1,
                  pointerEvents: state.isDisabled ? 'none' : 'auto',
                }),
                singleValue: base => ({
                  ...base,
                  color: '#6c757d',
                }),
                placeholder: base => ({
                  ...base,
                  color: '#6c757d',
                }),
              }}
              singlePath
            />
          </FormGroup>
        </Col>

        <Col lg={6} className="px-0 pl-lg-2">
          <FormGroup>
            <Label>Cidade</Label>
            <InputSelect
              isDisabled={disabled}
              name="city"
              options={cityList.cityList}
              onChange={selectedOption => {
                clearError('address.city');
                setSelectedCity(selectedOption);
              }}
              value={selectedCity}
              placeholder="Cidade"
              styles={{
                control: (base, state) => ({
                  ...base,
                  backgroundColor: state.isDisabled
                    ? '#ced4da9f !important'
                    : '#fff',
                  cursor: state.isDisabled ? 'not-allowed' : base.cursor,
                  opacity: state.isDisabled ? 0.65 : 1,
                  pointerEvents: state.isDisabled ? 'none' : 'auto',
                }),
                singleValue: base => ({
                  ...base,
                  color: '#6c757d',
                }),
                placeholder: base => ({
                  ...base,
                  color: '#6c757d',
                }),
              }}
              singlePath
            />
          </FormGroup>
        </Col>
      </Row>
    </Scope>
  );
}
